import '../PageLayout/Page.css';
import './Sidebar.css';

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sidebarItems } from '../../Data/data';
import SidebarItem from './SidebarItem';
import { getRolesFromStorage, hasAnyRole, subscribeToRoles } from '../../services/clientAuth';
import NotificationBell from '../NotificationBell/NotificationBell';

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [breakpoint]);

    return isMobile;
}

function getInitialTheme() {
    try {
        const stored = localStorage.getItem('theme');
        if (stored === 'dark' || stored === 'light') return stored;
    } catch {
        // ignore
    }
    return 'light';
}

export default function Sidebar() {
    const { pathname } = useLocation();
    const isMobile = useIsMobile(768);
    const [roles, setRoles] = useState(() => getRolesFromStorage());
    const [open, setOpen] = useState(false);
    const [theme, setTheme] = useState(getInitialTheme);

    const visibleItems = useMemo(
        () => sidebarItems.filter((item) => hasAnyRole(roles, item.allowedRoles)),
        [roles]
    );
    const fullThemeToggleLabel = theme === 'dark'
        ? 'Переключить на светлую тему'
        : 'Переключить на темную тему';
    const topbarThemeLabel = 'Тема';
    const sidebarThemeLabel = theme === 'dark' ? 'Светлая тема' : 'Темная тема';

    useEffect(() => {
        setRoles(getRolesFromStorage());
        return subscribeToRoles(setRoles);
    }, []);

    useEffect(() => {
        document.body.setAttribute('data-theme', theme);
        try {
            localStorage.setItem('theme', theme);
        } catch {
            // ignore
        }
    }, [theme]);

    useEffect(() => {
        if (isMobile) setOpen(false);
    }, [pathname, isMobile]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        if (!isMobile) return undefined;
        if (!open) return undefined;

        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = prev;
        };
    }, [open, isMobile]);

    const sidebarOpenAttr = isMobile ? String(open) : 'true';

    return (
        <>
            {isMobile && (
                <div className="sidebarTopbar">
                    <button
                        className="burgerBtn"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? 'Закрыть меню' : 'Открыть меню'}
                        aria-expanded={open}
                        aria-controls="sidebar"
                    >
                        <span className="burgerLines"><span /></span>
                    </button>

                    <div className="topbarTitle">
                        <strong>Чио Чио</strong>
                        <small>Дизайн</small>
                    </div>

                    <div className="topbarActions">
                        <NotificationBell />
                        <button
                            className="themeBtn"
                            onClick={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
                            aria-label={fullThemeToggleLabel}
                            title={fullThemeToggleLabel}
                            type="button"
                        >
                            {topbarThemeLabel}
                        </button>
                    </div>
                </div>
            )}

            {isMobile && open && (
                <div
                    className="sidebarOverlay"
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                />
            )}

            <nav
                id="sidebar"
                className="pt-m pb-m"
                data-open={sidebarOpenAttr}
                aria-hidden={isMobile ? !open : undefined}
            >
                <div className="sidebarHeader">
                    <div className="txt-l txt-bold">Чио Чио</div>
                    <div className="txt-s txt-thin mb-m">Дизайн</div>
                    <div className="sidebarHeaderActions">
                        <NotificationBell />
                        <button
                            className="themeBtn"
                            onClick={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
                            aria-label={fullThemeToggleLabel}
                            title={fullThemeToggleLabel}
                            type="button"
                        >
                            {sidebarThemeLabel}
                        </button>
                    </div>
                    <hr />
                </div>

                <div className="sidebarItems">
                    {visibleItems.map((el) => (
                        <SidebarItem
                            key={el.id}
                            item={el}
                            onNavigate={() => {
                                if (isMobile) setOpen(false);
                            }}
                        />
                    ))}
                </div>
            </nav>
        </>
    );
}
